import { HealthRow, HealthTable, Skeleton, type HealthRowData } from "@omnabase/ui";
import { useNav, useWs } from "../../lib/nav";
import { useKnowledge, type Scope } from "../../lib/knowledge";
import type { KbHref } from "./KnowledgeScreen";

/** Project-agnostic: one table "Knowledge bases" (M14 round five, the founder: "tables instead of card lists"), one row per scope (Shared
 *  first, the prototype's order). M14 round six (the founder): the whole row leads into the scope (a scope without knowledge to its setup);
 *  Articles, Sources and Cited in drafts are single numbers; what needs attention is a marker beside the name whose tooltip names the count
 *  and what. The numbers are the API's (`health`, computed in shared). */
export function Overview({ scopes, kbHref }: { scopes: Scope[]; kbHref: KbHref }) {
  return (
    <section className="ob-overview" aria-label="Knowledge base overview">
      <h1 className="ob-overview__title">Overview</h1>
      <p className="ob-overview__intro">What the AI reads before it answers, per product, plus Shared for what applies to all of them.</p>
      <HealthTable label="Knowledge bases">{scopes.map((s) => <ScopeRow key={s.id} scope={s} kbHref={kbHref} />)}</HealthTable>
    </section>
  );
}

function ScopeRow({ scope: p, kbHref }: { scope: Scope; kbHref: KbHref }) {
  const nav = useNav(); const { slug } = useWs();
  const { data: o } = useKnowledge(slug, p.id);
  if (!o) return <ScopeRowSkeleton name={p.name} />;
  const h = o.health;
  const base = { id: p.id, initials: p.name.slice(0, 2).toUpperCase(), name: p.name, href: kbHref("/knowledge/articles", { project: p.id }) };
  if (h.empty) return <HealthRow row={{ ...base, meta: p.shared ? "Read for every project · no knowledge base yet" : "No knowledge base yet", empty: true }} onNavigate={nav} />;
  const warnings = h.notes.filter((n) => n.kind === "warn").map((n) => n.text);
  const row: HealthRowData = {
    ...base, meta: p.shared ? "Read for every project; where they differ, the project wins" : `${h.readable} items readable by the AI`,
    articles: h.articles, sources: h.sources, cited: h.cited, sizePct: h.sizePct,
    attention: warnings.length ? { count: warnings.length, what: warnings } : undefined,
  };
  return <HealthRow row={row} onNavigate={nav} />;
}

/** a scope's row while its numbers load (polish round two): the name stays, grey, still blocks where the numbers will be (the table's own
 *  six columns) */
function ScopeRowSkeleton({ name }: { name: string }) {
  return (
    <tr className="ob-dtable__row ob-health-row" aria-label={name} aria-busy="true">
      <td className="ob-dtable__grow"><Skeleton shape="line" width="50%" /></td>
      <td className="ob-dtable__num"><Skeleton shape="line" width="2em" /></td>
      <td className="ob-dtable__num ob-dtable__col--below-540"><Skeleton shape="line" width="2em" /></td>
      <td className="ob-dtable__num ob-dtable__col--below-820"><Skeleton shape="line" width="2em" /></td>
      <td className="ob-dtable__col--below-680"><Skeleton shape="line" width="60%" /></td>
      <td />
    </tr>
  );
}
